import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { ListTicketsQuery } from '@/types/api';

/**
 * V4.9.5+ — bi-directional URL ↔ filter state sync.
 *
 * Used by every ticket-list page (All Tickets, My Queue, Approvals, Archives,
 * Deleted, My Tags) so the user's chosen sort / status / category / store /
 * severity / page / per_page are all kept in the URL query string.
 *
 * Why: clicking a ticket row navigates to /tickets/:id with a `from` state
 * that includes the FULL pathname + search ("?sort=created_at.desc&page=2").
 * Clicking ← Back on the detail page restores the exact view. Previously
 * filters were held in component state only, so the back button always
 * dropped the user on the page's default Smart sort — losing every choice
 * they'd made before opening a ticket. (Ops-reported bug, V4.9.5+.)
 */
export function parseListTicketsQueryFromSearchParams(
  params: URLSearchParams,
  defaults: Partial<ListTicketsQuery> = {}
): ListTicketsQuery {
  const csv = (key: string) => {
    const raw = params.get(key);
    if (!raw) return undefined;
    return raw.split(',').map((v) => v.trim()).filter(Boolean);
  };
  const created = params.get('created');
  const createdAfter = (() => {
    if (created === 'today') {
      const d = new Date();
      d.setHours(0, 0, 0, 0);
      return d.toISOString();
    }
    return params.get('created_after') ?? undefined;
  })();
  return {
    page: Number(params.get('page')) || defaults.page || 1,
    per_page: Number(params.get('per_page')) || defaults.per_page || 25,
    sort: params.get('sort') ?? defaults.sort ?? 'smart',
    status: csv('status') as ListTicketsQuery['status'],
    category: csv('category') as ListTicketsQuery['category'],
    store: csv('store') as ListTicketsQuery['store'],
    severity: csv('severity') as ListTicketsQuery['severity'],
    breach_flag: csv('breach_flag') as ListTicketsQuery['breach_flag'],
    // V4.9.12 — Dashboard drill-down filters (open / approval_state).
    open: params.get('open') === '1' ? true : undefined,
    approval_state: csv('approval_state') as ListTicketsQuery['approval_state'],
    search: params.get('search') ?? undefined,
    created_after: createdAfter,
    // V4.9.10 — created_before for the date range filter.
    created_before: params.get('created_before') ?? undefined
  };
}

export function listTicketsQueryToSearchParams(
  q: ListTicketsQuery,
  defaults: Partial<ListTicketsQuery> = {}
): URLSearchParams {
  const params = new URLSearchParams();
  if (q.search) params.set('search', q.search);
  if (q.status && (q.status as string[]).length) {
    params.set('status', (q.status as string[]).join(','));
  }
  if (q.category && (q.category as string[]).length) {
    params.set('category', (q.category as string[]).join(','));
  }
  if (q.store && (q.store as string[]).length) {
    params.set('store', (q.store as string[]).join(','));
  }
  if (q.severity && (q.severity as string[]).length) {
    params.set('severity', (q.severity as string[]).join(','));
  }
  if (q.breach_flag && (q.breach_flag as string[]).length) {
    params.set('breach_flag', (q.breach_flag as string[]).join(','));
  }
  // V4.9.12 — Dashboard drill-down filters. Kept in the URL so they survive
  // back/forward nav and so the filter bar's spread-based onChange doesn't
  // drop them when the user tweaks another filter on the landed page.
  if (q.open) params.set('open', '1');
  if (q.approval_state && (q.approval_state as string[]).length) {
    params.set('approval_state', (q.approval_state as string[]).join(','));
  }
  const defaultSort = defaults.sort ?? 'smart';
  if (q.sort && q.sort !== defaultSort) params.set('sort', q.sort);
  const defaultPage = defaults.page ?? 1;
  if (q.page && q.page !== defaultPage) params.set('page', String(q.page));
  const defaultPerPage = defaults.per_page ?? 25;
  if (q.per_page && q.per_page !== defaultPerPage) params.set('per_page', String(q.per_page));
  if (q.created_after) params.set('created_after', q.created_after);
  // V4.9.10 — created_before for the date range filter.
  if (q.created_before) params.set('created_before', q.created_before);
  return params;
}

/**
 * Hook that wires a ListTicketsQuery state to the page's URL query string.
 * Reads the initial state from the URL (so deep links work), pushes every
 * subsequent change back into the URL with `replace: true` (so we don't
 * spam the browser history), and re-syncs when the user clicks back /
 * forward.
 *
 * Pass `defaults` so page-specific defaults (e.g. My Queue uses per_page=100
 * instead of 25) are correctly omitted from the URL.
 */
export function useFiltersInUrl(defaults: Partial<ListTicketsQuery> = {}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const initial = useMemo(
    () => parseListTicketsQueryFromSearchParams(searchParams, defaults),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
  const [filters, setFilters] = useState<ListTicketsQuery>(initial);

  // URL → state (back/forward, deep links). Avoid loops by JSON-comparing.
  useEffect(() => {
    const fromUrl = parseListTicketsQueryFromSearchParams(searchParams, defaults);
    if (JSON.stringify(fromUrl) !== JSON.stringify(filters)) {
      setFilters(fromUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // state → URL on every change. replace:true so each filter tweak doesn't
  // create a history entry — only the route-level navigations should.
  useEffect(() => {
    const next = listTicketsQueryToSearchParams(filters, defaults);
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  return [filters, setFilters] as const;
}
